import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Loader2, RotateCcw, AlertTriangle, Settings as SettingsIcon } from "lucide-react";
import Markdown from "@/components/Markdown";
import { useAIStore } from "@/store/aiStore";
import { getProvider, AIError, type FeedbackRequest } from "@/core/ai";

interface AIFeedbackPanelProps {
  /** Built lazily at click time so it picks up the latest written response. */
  buildRequest: () => FeedbackRequest;
}

type Status = "idle" | "loading" | "done" | "error";

export default function AIFeedbackPanel({ buildRequest }: AIFeedbackPanelProps) {
  const configured = useAIStore((s) => s.isConfigured());
  const apiKey = useAIStore((s) => s.apiKey);
  const model = useAIStore((s) => s.model);

  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const abort = useRef<AbortController | null>(null);

  useEffect(() => () => abort.current?.abort(), []);

  async function run() {
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    setStatus("loading");
    setError("");
    try {
      const provider = getProvider({ apiKey: apiKey.trim(), model });
      const md = await provider.feedback(buildRequest(), controller.signal);
      if (controller.signal.aborted) return;
      setFeedback(md);
      setStatus("done");
    } catch (e) {
      if (controller.signal.aborted || (e instanceof DOMException && e.name === "AbortError")) return;
      setError(e instanceof AIError ? e.message : "Something went wrong getting feedback.");
      setStatus("error");
    }
  }

  // Not set up yet — nudge toward Settings rather than showing a dead button.
  if (!configured) {
    return (
      <Link
        to="/settings"
        className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-muted/80 transition-colors hover:text-text"
      >
        <Sparkles className="h-3.5 w-3.5" />
        Enable AI feedback
        <SettingsIcon className="h-3 w-3" />
      </Link>
    );
  }

  if (status === "idle") {
    return (
      <button
        onClick={run}
        className="ml-auto inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-2 text-sm font-medium text-text transition-colors hover:bg-primary/20"
      >
        <Sparkles className="h-4 w-4 text-primary" />
        Get AI feedback
      </button>
    );
  }

  return (
    <div className="mt-4 w-full">
      <AnimatePresence mode="wait">
        {status === "loading" && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-sm text-muted"
          >
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            Asking your AI mentor for feedback…
          </motion.div>
        )}

        {status === "error" && (
          <motion.div
            key="error"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex items-start gap-2 rounded-xl border border-amber-400/40 bg-amber-400/5 px-4 py-3 text-sm"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
            <div className="flex-1">
              <p className="text-text">{error}</p>
              <button onClick={run} className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-amber-400 hover:underline">
                <RotateCcw className="h-3 w-3" /> Try again
              </button>
            </div>
          </motion.div>
        )}

        {status === "done" && (
          <motion.div
            key="done"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="rounded-xl border border-primary/30 bg-primary/5 p-5"
          >
            <div className="mb-2 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wide text-primary">AI feedback</span>
              <button
                onClick={run}
                className="ml-auto inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted transition-colors hover:bg-surface-2 hover:text-text"
              >
                <RotateCcw className="h-3 w-3" /> Regenerate
              </button>
            </div>
            <div className="text-sm leading-relaxed text-text">
              <Markdown source={feedback} />
            </div>
            <p className="mt-3 border-t border-border/60 pt-2 text-[11px] text-muted/70">
              AI-generated — treat as one perspective, not a verdict.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
